import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, Upload, Play, Pause, Download, Loader2, ExternalLink, Zap,
  Mic2, Music2, Lock, Search, CheckCircle2, AlertCircle, ArrowRight, Send,
  RefreshCw, Film, X, Radio,
} from "lucide-react";
import { api, APP_ORIGIN } from "@/lib/api";
import KaraokeSharePanel from "@/components/production/KaraokeSharePanel";
import BroadcastEverywhereModal from "@/components/production/BroadcastEverywhereModal";

/**
 * AI Vocalist Studio — Kits AI BYOK voice-to-voice conversion.
 *
 * Sing or hum a scratch vocal → route it through a licensed, artist-consented
 * voice model. Every generation is 1 Kits AI credit on the user's own key.
 *
 * Layout mirrors the Stem Studio: connection banner → voice-model grid →
 * generate panel → recent generations. All backend calls are BYOK-gated
 * against the caller's Kits AI license key (see `/api/vocalist/*`).
 */

const OUTPUT_DESTINATIONS = [
  { id: "asset_library",    label: "Asset Library only",   icon: Music2 },
  { id: "production_suite", label: "Production Suite",     icon: Zap },
  { id: "imaging_studio",   label: "Imaging Studio",       icon: Mic2 },
];

const STATUS_COLOR = {
  processing: "#4a9eff",
  succeeded:  "#3ecf8e",
  failed:     "#f87171",
};

const isTerminal = (s) => ["succeeded", "failed", "canceled"].includes((s || "").toLowerCase());


// ── Deterministic waveform strip (same visual as Stem Studio) ────────
function useWaveformBars(seed = 0, count = 46) {
  return useMemo(() => {
    let s = seed || 42;
    const bars = [];
    for (let i = 0; i < count; i++) {
      s = (s * 9301 + 49297) % 233280;
      const rand = s / 233280;
      const h = 0.25 + 0.6 * Math.abs(Math.sin(i * 0.45) + (rand - 0.5) * 0.6);
      bars.push(Math.max(0.12, Math.min(1.0, h)));
    }
    return bars;
  }, [seed, count]);
}
function WaveformStrip({ seedFrom = "wave", color = "#a78bfa", height = 28 }) {
  const seed = useMemo(() => {
    let acc = 0;
    for (let i = 0; i < seedFrom.length; i++) acc += seedFrom.charCodeAt(i) * (i + 1);
    return acc;
  }, [seedFrom]);
  const bars = useWaveformBars(seed);
  return (
    <div className="flex items-center gap-[2px] rounded-md px-2 overflow-hidden flex-1"
         style={{ height, background: "#161923" }}>
      {bars.map((h, i) => (
        <div key={i}
             style={{
               width: "2.5px",
               height: `${Math.round(h * (height - 4))}px`,
               background: color,
               opacity: 0.7 + 0.3 * (i % 3 === 0 ? 1 : 0),
               borderRadius: 2,
             }} />
      ))}
    </div>
  );
}


// ── Locked-screen ────────────────────────────────────────────────────
function LockedScreen({ access }) {
  const navigate = useNavigate();
  const isTrialExpired = access?.reason === "trial_expired";
  return (
    <div data-testid="vocalist-locked"
         className="mx-auto max-w-3xl mt-16 rounded-2xl border p-10 text-center"
         style={{ background: "#161923", borderColor: "#232632" }}>
      <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full"
           style={{ background: "rgba(167,139,250,0.12)" }}>
        <Lock size={28} style={{ color: "#a78bfa" }} />
      </div>
      <div className="type-h1 mb-3 text-white">
        {isTrialExpired ? "Your trial has ended" : "AI Vocalist Studio is a Pro Radio feature"}
      </div>
      <div className="type-body text-neutral-400 mb-8">
        {isTrialExpired
          ? "Upgrade to keep generating vocals with licensed Kits AI voice models."
          : "Generate sung vocals with licensed, artist-consented voice models. Available on Pro Radio, Network, Enterprise, and Trial plans."}
      </div>
      <div className="flex flex-wrap gap-2 justify-center mb-8">
        {["140+ licensed voices", "1-click voice conversion",
          "Send to DAW / Imaging", "No takedown risk"].map((f) => (
          <span key={f} className="rounded-full px-3 py-1 type-caption"
                style={{ background: "rgba(74,158,255,0.10)", color: "#4a9eff",
                         border: "1px solid rgba(74,158,255,0.3)" }}>
            {f}
          </span>
        ))}
      </div>
      <button data-testid="vocalist-upgrade-btn"
              onClick={() => navigate("/dashboard/billing?tab=plan")}
              className="popular-pulse rounded-full px-6 py-3 font-medium text-white transition"
              style={{ background: "linear-gradient(135deg, #4a9eff 0%, #a78bfa 100%)" }}>
        Upgrade to Pro Radio
      </button>
    </div>
  );
}


// ── Voice card ───────────────────────────────────────────────────────
function VoiceCard({ voice, selected, onSelect, onPreview, previewing }) {
  const initials = (voice.title || "??")
    .split(/[^A-Za-z]+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "").join("") || "AI";
  const genre = voice.tags?.[0] || "Vocal";
  const showDemo = !!voice.demo_url;
  return (
    <div data-testid={`vocalist-voice-${voice.id}`}
         onClick={() => onSelect(voice)}
         className="cursor-pointer rounded-xl border p-4 transition hover:-translate-y-[1px]"
         style={{
           // Update #166 — blue (#4a9eff) selection to match mockup, and
           // a subtle top-lit gradient so the card feels lifted when picked.
           background: selected
             ? "linear-gradient(180deg, rgba(74,158,255,0.12), #1a1e2a 60%)"
             : "#1a1e2a",
           borderColor: selected ? "#4a9eff" : "#232632",
           boxShadow: selected ? "0 0 0 1px rgba(74,158,255,0.35)" : "none",
         }}>
      <div className="flex items-start gap-3 mb-3">
        {voice.image_url ? (
          <img src={voice.image_url} alt="" className="h-11 w-11 rounded-full object-cover"
               style={{ border: "1px solid #232632" }} />
        ) : (
          <div className="h-11 w-11 rounded-full flex items-center justify-center type-caption font-semibold"
               style={{ background: "#232632", color: "#4a9eff" }}>
            {initials}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-white truncate" style={{ fontSize: 14, fontWeight: 600 }}>
            {voice.title}
          </div>
          <div className="type-caption text-neutral-400 truncate">{genre}</div>
        </div>
        {showDemo && (
          <button
            data-testid={`vocalist-preview-${voice.id}`}
            onClick={(e) => { e.stopPropagation(); onPreview(voice); }}
            className="rounded-full h-8 w-8 flex items-center justify-center transition"
            style={{
              background: previewing ? "#4a9eff" : "rgba(74,158,255,0.14)",
              color: previewing ? "#ffffff" : "#4a9eff",
            }}
            title="Preview voice demo">
            {previewing ? <Pause size={14} /> : <Play size={14} />}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1">
        <span className="rounded px-1.5 py-0.5 type-caption"
              style={{ background: "rgba(62,207,142,0.14)", color: "#3ecf8e" }}>
          Licensed
        </span>
        {voice.gender && (
          <span className="rounded px-1.5 py-0.5 type-caption"
                style={{ background: "#232632", color: "#c4c9d4" }}>
            {voice.gender}
          </span>
        )}
        {(voice.tags || []).slice(0, 2).map((t) => (
          <span key={t} className="rounded px-1.5 py-0.5 type-caption"
                style={{ background: "#232632", color: "#a9b3c9" }}>
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}


// ── Length preset card ───────────────────────────────────────────────
// Purple (#7c5cff) selection to differentiate from voice cards. Matches
// the mockup's "sweeper / jingle / promo" categorisation.
function LengthPresetCard({ preset, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(preset.id)}
      data-testid={`vocalist-length-${preset.id}`}
      className="text-left rounded-xl border p-4 transition hover:-translate-y-[1px]"
      style={{
        background: selected
          ? "linear-gradient(180deg, rgba(124,92,255,0.14), #1a1e2a 60%)"
          : "#1a1e2a",
        borderColor: selected ? "#7c5cff" : "#232632",
        boxShadow: selected ? "0 0 0 1px rgba(124,92,255,0.35)" : "none",
      }}
    >
      <span className="rounded-full px-2 py-0.5 type-caption font-mono uppercase tracking-wider"
            style={{ background: "rgba(124,92,255,0.14)", color: "#7c5cff", fontSize: 10 }}>
        {preset.min}–{preset.max}s
      </span>
      <div className="text-white mt-2" style={{ fontSize: 14, fontWeight: 600 }}>
        {preset.label}
      </div>
      <div className="type-caption text-neutral-400 mt-1">{preset.blurb}</div>
    </button>
  );
}


// ── Recent generation row ────────────────────────────────────────────
function GenerationRow({ job, onSendTo }) {
  const navigate = useNavigate();
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  // Lyric-video (karaoke MP4) state
  const [kkJob, setKkJob] = useState(null);   // { job_id, status, progress_step, video_url, error }
  const [kkBusy, setKkBusy] = useState(false);
  const [kkPreview, setKkPreview] = useState(false);
  const kkPollRef = useRef(null);

  // Update #143 — "Broadcast this everywhere" super-button state
  const [beOpen, setBeOpen] = useState(false);

  const audioUrl = job?.output?.audio_url ? `${APP_ORIGIN}${job.output.audio_url}` : null;
  const kkVideoUrl = kkJob?.video_url ? `${APP_ORIGIN}${kkJob.video_url}` : null;

  const toggle = () => {
    if (!audioUrl) return;
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(audioUrl);
    audioRef.current = a;
    a.play().catch(() => toast.error("Playback failed"));
    a.onended = () => setPlaying(false);
    setPlaying(true);
  };

  // ── Karaoke / lyric-video generation from an AI-vocalist output ──
  const pollKaraokeJob = useCallback(async (kkJobId) => {
    try {
      const r = await api.get(`/production/karaoke/${kkJobId}`);
      const d = r.data || {};
      setKkJob(d);
      if (["succeeded", "failed"].includes(d.status)) {
        setKkBusy(false);
        if (d.status === "succeeded") {
          toast.success("Lyric video ready — preview or download below.");
        } else {
          toast.error(d.error || "Lyric video render failed");
        }
        return;
      }
      // Keep polling
      kkPollRef.current = setTimeout(() => pollKaraokeJob(kkJobId), 4000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setKkBusy(false);
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Failed to poll lyric video"));
    }
  }, []);

  const runKaraoke = async () => {
    if (!job?.output?.asset_id) {
      toast.error("Source vocal not persisted yet");
      return;
    }
    setKkBusy(true);
    setKkJob({ status: "queued", progress_step: "queued" });
    try {
      const r = await api.post("/production/karaoke", {
        asset_id:              job.output.asset_id,
        style:                 "gradient",
        vocal_only:            true,
        source_label_override: job.project_name || `AI Vocal — ${job.voice_model_title}`,
      });
      const kkJobId = r?.data?.job_id;
      if (!kkJobId) throw new Error("No job_id returned");
      toast.message("Rendering lyric video… transcribing + burning captions.");
      setKkJob({ job_id: kkJobId, status: "queued", progress_step: "queued" });
      pollKaraokeJob(kkJobId);
    } catch (e) {
      setKkBusy(false);
      const detail = e?.response?.data?.detail;
      if (detail?.error === "karaoke_locked") {
        toast.error(detail.message || "Upgrade to render lyric videos", {
          action: { label: "See plans", onClick: () => navigate("/#pricing") },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Karaoke render failed"));
      }
    }
  };

  useEffect(() => () => {
    if (kkPollRef.current) clearTimeout(kkPollRef.current);
  }, []);

  const created = job.created_at ? new Date(job.created_at).toLocaleString() : "";
  const isDone = job.status === "succeeded";
  const isFailed = job.status === "failed";
  const kkStatus = kkJob?.status;
  const kkInProgress = kkBusy || (kkStatus && !["succeeded", "failed"].includes(kkStatus));

  return (
    <div data-testid={`vocalist-history-${job.job_id}`}
         className="rounded-xl border p-3"
         style={{ background: "#1a1e2a", borderColor: "#232632" }}>
      <div className="flex items-center gap-3">
        <div
          className="h-10 w-10 rounded-lg flex items-center justify-center"
          style={{ background: "rgba(167,139,250,0.10)", color: "#a78bfa" }}
        >
          <Mic2 size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white truncate" style={{ fontSize: 14, fontWeight: 500 }}>
            {job.project_name || `AI Vocal — ${job.voice_model_title}`}
          </div>
          <div className="type-caption text-neutral-400 truncate">
            {job.voice_model_title} · {created}
          </div>
        </div>
        <WaveformStrip seedFrom={job.job_id} color={STATUS_COLOR[job.status] || "#a78bfa"} />
        <span data-testid={`vocalist-history-status-${job.job_id}`}
              className="rounded-full px-2 py-0.5 type-caption capitalize"
              style={{
                background: `${STATUS_COLOR[job.status] || "#4a9eff"}22`,
                color: STATUS_COLOR[job.status] || "#4a9eff",
              }}>
          {job.status === "processing" ? "Rendering…" : job.status}
        </span>
        <div className="flex items-center gap-1.5">
          {isDone && audioUrl && (
            <button data-testid={`vocalist-play-${job.job_id}`} onClick={toggle}
                    className="rounded-full h-8 w-8 flex items-center justify-center"
                    style={{ background: "rgba(74,158,255,0.14)", color: "#4a9eff" }}>
              {playing ? <Pause size={14} /> : <Play size={14} />}
            </button>
          )}
          {isDone && audioUrl && (
            <a data-testid={`vocalist-download-${job.job_id}`}
               href={audioUrl} download={`vocal-${job.job_id}.wav`}
               className="rounded-full h-8 w-8 flex items-center justify-center"
               style={{ background: "rgba(62,207,142,0.14)", color: "#3ecf8e" }}>
              <Download size={14} />
            </a>
          )}
          {isDone && !kkJob && (
            <button data-testid={`vocalist-karaoke-${job.job_id}`}
                    onClick={runKaraoke}
                    disabled={kkBusy}
                    className="rounded-full px-2.5 py-1 type-caption disabled:opacity-60"
                    style={{ background: "rgba(255,138,31,0.14)", color: "#ff8a1f" }}
                    title="Render as a lyric video (MP4) with synced captions">
              <Film size={12} className="inline mr-1" /> Lyric Video
            </button>
          )}
          {isDone && (
            <button data-testid={`vocalist-broadcast-${job.job_id}`}
                    onClick={() => setBeOpen(true)}
                    className="rounded-full px-2.5 py-1 type-caption"
                    style={{ background: "linear-gradient(90deg, rgba(255,138,31,0.18), rgba(255,107,53,0.18))",
                             color: "#ff8a1f",
                             border: "0.5px solid rgba(255,138,31,0.35)" }}
                    title="One-click: render lyric video, publish WordPress post, share to socials">
              <Radio size={12} className="inline mr-1" /> Broadcast everywhere
            </button>
          )}
          {isDone && (
            <button data-testid={`vocalist-send-daw-${job.job_id}`}
                    onClick={() => onSendTo(job, "production_suite")}
                    className="rounded-full px-2.5 py-1 type-caption"
                    style={{ background: "rgba(74,158,255,0.14)", color: "#4a9eff" }}>
              Send to DAW
            </button>
          )}
          {isFailed && (
            <button onClick={() => toast.error(job.error || "Unknown error")}
                    className="rounded-full h-8 w-8 flex items-center justify-center"
                    style={{ background: "rgba(248,113,113,0.14)", color: "#f87171" }}
                    title={job.error || "Failed"}>
              <AlertCircle size={14} />
            </button>
          )}
        </div>
      </div>

      {/* ── Karaoke / lyric-video sub-panel ─────────────────── */}
      {kkJob && (
        <div data-testid={`vocalist-karaoke-panel-${job.job_id}`}
             className="mt-3 rounded-lg border p-3 flex items-center gap-3 flex-wrap"
             style={{ background: "#161923", borderColor: "#232632" }}>
          <span className="rounded px-2 py-0.5 type-caption font-semibold"
                style={{ background: "rgba(255,138,31,0.15)", color: "#ff8a1f" }}>
            LYRIC VIDEO
          </span>
          {kkInProgress ? (
            <>
              <Loader2 size={14} className="animate-spin" style={{ color: "#ff8a1f" }} />
              <span className="type-caption text-neutral-400 flex-1"
                    data-testid={`vocalist-karaoke-progress-${job.job_id}`}>
                {kkJob.progress_step === "vocals_split"     && "Splitting vocals…"}
                {kkJob.progress_step === "load_vocals"      && "Loading AI vocal track…"}
                {kkJob.progress_step === "compress_vocals"  && "Compressing for transcription…"}
                {kkJob.progress_step === "transcribe"       && "Transcribing lyrics word-by-word…"}
                {kkJob.progress_step === "build_subs"       && "Building karaoke subtitles…"}
                {kkJob.progress_step === "render_mp4"       && "Rendering karaoke MP4…"}
                {(!kkJob.progress_step || kkJob.progress_step === "queued") && "Queued…"}
              </span>
            </>
          ) : kkJob.status === "succeeded" && kkVideoUrl ? (
            <>
              <span className="type-caption text-neutral-300 flex-1">
                Lyric video ready ({(kkJob.duration_s || 0).toFixed(1)}s ·{" "}
                {kkJob.language ? kkJob.language.toUpperCase() : "?"})
              </span>
              <button data-testid={`vocalist-karaoke-preview-${job.job_id}`}
                      onClick={() => setKkPreview((v) => !v)}
                      className="rounded-full px-3 py-1 type-caption"
                      style={{ background: "rgba(74,158,255,0.14)", color: "#4a9eff" }}>
                {kkPreview ? "Hide preview" : "Preview"}
              </button>
              <a data-testid={`vocalist-karaoke-download-${job.job_id}`}
                 href={kkVideoUrl}
                 download={`lyric-video-${job.job_id}.mp4`}
                 className="rounded-full px-3 py-1 type-caption"
                 style={{ background: "rgba(62,207,142,0.14)", color: "#3ecf8e" }}>
                <Download size={12} className="inline mr-1" /> MP4
              </a>
            </>
          ) : kkJob.status === "failed" ? (
            <>
              <AlertCircle size={14} style={{ color: "#f87171" }} />
              <span className="type-caption flex-1" style={{ color: "#f87171" }}>
                Lyric video failed: {kkJob.error || "Unknown"}
              </span>
              <button onClick={() => { setKkJob(null); }}
                      className="rounded-full px-2 py-0.5 type-caption"
                      style={{ background: "#232632", color: "#a9b3c9" }}>
                Dismiss
              </button>
            </>
          ) : null}
        </div>
      )}

      {kkPreview && kkVideoUrl && (
        <div className="mt-2">
          <video data-testid={`vocalist-karaoke-video-${job.job_id}`}
                 src={kkVideoUrl}
                 controls
                 className="w-full rounded-lg"
                 style={{ maxHeight: 360, background: "#000" }} />
        </div>
      )}

      {/* Reusable share-to-socials panel — reuses Update #123's dispatcher */}
      {kkJob?.status === "succeeded" && kkJob.job_id && (
        <KaraokeSharePanel
          jobId={kkJob.job_id}
          defaultCaption={
            `🎤 ${job.project_name || `AI Vocal — ${job.voice_model_title}`}\n\n` +
            `Fresh lyric video from the studio.\n\n` +
            `#LyricVideo #Radio #BroadcastFlow`
          }
          testidPrefix={`vocalist-share-${job.job_id}`}
        />
      )}

      {/* Update #143 — Broadcast Everywhere super-button modal */}
      <BroadcastEverywhereModal
        open={beOpen}
        onClose={() => setBeOpen(false)}
        assetId={job?.output?.asset_id || ""}
        sourceLabel={job?.project_name || `AI Vocal — ${job?.voice_model_title || "track"}`}
        vocalOnly
        testidPrefix={`vocalist-be-${job.job_id}`}
      />
    </div>
  );
}


// ── Main page ────────────────────────────────────────────────────────
export default function VocalistStudio() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const previewAudioRef = useRef(null);
  const pollRef = useRef(null);

  const [access, setAccess] = useState(null);
  const [voices, setVoices] = useState([]);
  const [voicesMeta, setVoicesMeta] = useState({});
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);

  // Update #166 — tab bar (Licensed / My Trained / History) + length presets
  const [activeTab, setActiveTab] = useState("licensed");   // 'licensed' | 'trained' | 'history'
  const [presets, setPresets] = useState([]);
  const [hardCapSec, setHardCapSec] = useState(30);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [maxOutputLength, setMaxOutputLength] = useState(30);

  const [styles, setStyles] = useState([]);
  const [selectedStyle, setSelectedStyle] = useState("match");
  const [destination, setDestination] = useState("asset_library");
  const [projectName, setProjectName] = useState("");
  const [scratchFile, setScratchFile] = useState(null);
  const [scratchDuration, setScratchDuration] = useState(0);
  const [generating, setGenerating] = useState(false);

  const [jobs, setJobs] = useState([]);

  const generatePanelRef = useRef(null);

  // ── Access on mount ─────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/vocalist/access");
        setAccess(r.data || { allowed: false, reason: "unknown" });
      } catch {
        setAccess({ allowed: false, reason: "network_error" });
      }
    })();
  }, []);

  // ── Load styles + jobs + first page of voices when access is allowed ─
  useEffect(() => {
    if (!access?.allowed) return;
    (async () => {
      try {
        const r = await api.get("/vocalist/styles");
        setStyles(r.data?.styles || []);
      } catch { /* noop */ }
      try {
        const r2 = await api.get("/vocalist/length-presets");
        const list = r2.data?.presets || [];
        setPresets(list);
        setHardCapSec(r2.data?.hard_cap_sec || 30);
        // Default to Jingle Hook — the mid-length option most sessions land on.
        if (list.length && !selectedPreset) {
          const jh = list.find((p) => p.id === "jingle_hook") || list[0];
          setSelectedPreset(jh.id);
          setMaxOutputLength(jh.default);
        }
      } catch { /* noop */ }
    })();
  }, [access?.allowed, selectedPreset]);

  const loadJobs = useCallback(async () => {
    try {
      const r = await api.get("/vocalist/jobs?limit=30");
      setJobs(r.data?.jobs || []);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { if (access?.allowed) loadJobs(); }, [access?.allowed, loadJobs]);

  // Poll while any job is not terminal
  const hasActive = jobs.some((j) => !isTerminal(j.status));
  useEffect(() => {
    if (!hasActive) return;
    pollRef.current = setTimeout(loadJobs, 3500);
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [hasActive, jobs.length, loadJobs]);

  const loadVoices = useCallback(async () => {
    if (!access?.allowed || !access?.kits_connected) return;
    if (activeTab === "history") return;   // history tab doesn't need the voice grid
    setLoadingVoices(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("per_page", "24");
      if (q.trim()) params.set("q", q.trim());
      // Update #166 — "My Trained Voices" tab flips Kits AI to the
      // caller's private catalog (voices they've cloned on their own
      // account). Licensed catalog remains the default surface.
      if (activeTab === "trained") params.set("my_models", "true");
      const r = await api.get(`/vocalist/voices?${params.toString()}`);
      setVoices(r.data?.voices || []);
      setVoicesMeta(r.data?.meta || {});
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === "byok_required") {
        // Access is fine but they haven't connected Kits AI yet
        setVoices([]);
      } else if (d?.error === "byok_invalid") {
        toast.error("Kits AI key was rejected — please reconnect", {
          action: { label: "Reconnect", onClick: () => navigate("/dashboard/providers") },
        });
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || "Failed to load voices"));
      }
    } finally {
      setLoadingVoices(false);
    }
  }, [access?.allowed, access?.kits_connected, page, q, navigate, activeTab]);

  useEffect(() => { loadVoices(); }, [loadVoices]);

  // ── Voice preview (demo_url) ────────────────────────────────────
  const previewVoice = (voice) => {
    if (previewingId === voice.id) {
      previewAudioRef.current?.pause();
      setPreviewingId(null);
      return;
    }
    if (previewAudioRef.current) previewAudioRef.current.pause();
    if (!voice.demo_url) return;
    const a = new Audio(voice.demo_url);
    previewAudioRef.current = a;
    a.play().catch(() => toast.error("Preview failed"));
    a.onended = () => setPreviewingId(null);
    setPreviewingId(voice.id);
  };

  // ── Scratch vocal picker — validates duration client-side against the
  //     30-sec broadcast cap before letting the file into the generate slot.
  const onPickScratch = useCallback((file) => {
    if (!file) { setScratchFile(null); setScratchDuration(0); return; }
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    audio.preload = "metadata";
    audio.onloadedmetadata = () => {
      const dur = Number(audio.duration || 0);
      URL.revokeObjectURL(url);
      if (dur && dur > hardCapSec + 0.5) {
        toast.error(
          `Scratch vocal is ${dur.toFixed(1)}s — Vocalist Studio is capped at ${hardCapSec}s. Trim before uploading.`
        );
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      setScratchFile(file);
      setScratchDuration(dur);
    };
    audio.onerror = () => {
      // Browser couldn't read the container — trust the user and let the
      // server validation catch it. This avoids blocking exotic-but-valid
      // formats on some Safari builds.
      URL.revokeObjectURL(url);
      setScratchFile(file);
      setScratchDuration(0);
    };
    audio.src = url;
  }, [hardCapSec]);

  // ── Generate ─────────────────────────────────────────────────────
  const generate = async () => {
    if (!selectedVoice) { toast.error("Pick a voice model first"); return; }
    if (!scratchFile) { toast.error("Upload a scratch vocal (WAV / MP3 / FLAC)"); return; }
    setGenerating(true);
    try {
      const fd = new FormData();
      fd.append("file", scratchFile);
      fd.append("voice_model_id", String(selectedVoice.id));
      fd.append("voice_model_title", selectedVoice.title || "");
      fd.append("style", selectedStyle);
      fd.append("output_destination", destination);
      if (projectName.trim()) fd.append("project_name", projectName.trim());
      if (selectedPreset) fd.append("length_preset", selectedPreset);
      if (maxOutputLength) fd.append("max_output_length", String(maxOutputLength));
      const r = await api.post("/vocalist/generate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const jid = r?.data?.job_id;
      if (jid) {
        toast.success(`Rendering with ${selectedVoice.title}… we'll poll every few seconds`);
        setScratchFile(null);
        setScratchDuration(0);
        if (fileInputRef.current) fileInputRef.current.value = "";
        loadJobs();
        // Nudge the user to the history area so they see the queued job.
        setTimeout(() => setActiveTab("history"), 400);
      } else {
        toast.error("Kits AI did not return a job id");
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === "byok_required") {
        toast.error("Connect your Kits AI license key first", {
          action: { label: "Connect", onClick: () => navigate("/dashboard/providers") },
        });
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || "Generation failed"));
      }
    } finally {
      setGenerating(false);
    }
  };

  const onSendTo = (job, target) => {
    if (target === "production_suite") {
      // Deep-link into the DAW with pending-clip payload
      try {
        const clip = {
          asset_id: job.output?.asset_id,
          audio_url: job.output?.audio_url,
          name: job.project_name || `AI Vocal — ${job.voice_model_title}`,
          target_track: "vo",
          source: "vocalist",
        };
        localStorage.setItem("ps_pending_clips", JSON.stringify([clip]));
      } catch { /* noop */ }
      toast.success("Opening Production Suite with vocal loaded…");
      navigate("/dashboard/production-suite");
    } else if (target === "imaging_studio") {
      toast.success("Vocal is in your Imaging library — opening…");
      navigate("/dashboard/imaging");
    }
  };

  // ── Render gates ───────────────────────────────────────────────
  if (access === null) {
    return (
      <div className="mx-auto max-w-5xl mt-24 text-center">
        <Loader2 className="mx-auto mb-3 animate-spin" size={22} />
        <div className="type-caption text-neutral-400">Loading Vocalist Studio…</div>
      </div>
    );
  }
  if (!access.allowed) return <LockedScreen access={access} />;

  return (
    <div data-testid="vocalist-studio-page"
         className="mx-auto max-w-6xl px-6 py-8" style={{ color: "#e6e9f0" }}>
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="type-h1 text-white">AI Vocalist Studio</div>
            <span data-testid="vocalist-beta-badge"
                  className="rounded-full px-2 py-0.5 type-caption font-semibold uppercase tracking-wide"
                  style={{ background: "rgba(124,92,255,0.15)", color: "#7c5cff" }}>
              Beta
            </span>
          </div>
          <div className="type-body text-neutral-400 max-w-xl">
            Generate sung vocals with licensed, artist-consented voice models.
            Turn a scratch vocal or hummed melody into a finished, royalty-cleared performance.
          </div>
        </div>
        {/* Update #166 — "New Vocal" CTA scrolls the user to the
            generate panel (matches mockup + saves a scroll on long pages). */}
        <button
          type="button"
          data-testid="vocalist-new-vocal-cta"
          onClick={() => {
            if (activeTab === "history") setActiveTab("licensed");
            setTimeout(() => {
              generatePanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 60);
          }}
          className="rounded-full px-4 py-2 font-medium text-white transition"
          style={{ background: "linear-gradient(135deg, #4a9eff 0%, #7c5cff 100%)" }}
        >
          <Sparkles size={14} className="inline mr-1.5" /> New Vocal
        </button>
      </div>

      {/* ── Connection banner ─────────────────────────────── */}
      <div data-testid="vocalist-connection-banner"
           className="rounded-xl border p-4 mb-6 flex items-center gap-3 flex-wrap"
           style={{ background: "#1a1e2a", borderColor: "#232632" }}>
        <div className="h-9 w-9 rounded-lg flex items-center justify-center font-semibold"
             style={{
               background: access.kits_connected ? "rgba(62,207,142,0.14)" : "rgba(248,113,113,0.14)",
               color: access.kits_connected ? "#3ecf8e" : "#f87171",
             }}>
          {access.kits_connected ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white" style={{ fontSize: 14, fontWeight: 600 }}>
            {access.kits_connected ? "Connected to Kits AI" : "Kits AI not connected"}
          </div>
          <div className="type-caption text-neutral-400">
            {access.kits_connected
              ? "Licensed, artist-consented voice models · revenue-shared with rights holders"
              : "Paste your Kits AI API key in the AI Provider Center to browse voices."}
          </div>
        </div>
        <button data-testid="vocalist-manage-connection"
                onClick={() => navigate("/dashboard/providers")}
                className="rounded-lg px-3 py-1.5 type-caption transition"
                style={{
                  background: access.kits_connected ? "#232632" : "#a78bfa",
                  color: access.kits_connected ? "#c4c9d4" : "#ffffff",
                }}>
          {access.kits_connected ? "Manage Connection" : "Connect Kits AI"}
        </button>
      </div>

      {/* ── Not connected: hint card ─────────────────────── */}
      {!access.kits_connected && (
        <div data-testid="vocalist-connect-hint"
             className="rounded-xl border p-6 text-center"
             style={{ background: "#161923", borderColor: "#232632" }}>
          <Lock size={22} className="mx-auto mb-3" style={{ color: "#a78bfa" }} />
          <div className="text-white mb-1" style={{ fontSize: 15, fontWeight: 600 }}>
            Connect Kits AI to browse 140+ licensed voices
          </div>
          <div className="type-caption text-neutral-400 mb-4">
            AI Vocalist Studio is BYOK — your Kits AI account, your credits, your voice-model library.
          </div>
          <button onClick={() => navigate("/dashboard/providers")}
                  className="rounded-full px-5 py-2 font-medium text-white transition"
                  style={{ background: "linear-gradient(135deg, #4a9eff 0%, #a78bfa 100%)" }}>
            Open AI Provider Center <ArrowRight size={14} className="inline ml-1" />
          </button>
        </div>
      )}

      {/* ── Connected: browse + generate ─────────────────── */}
      {access.kits_connected && (
        <>
          {/* Update #166 — 3-tab structure matches the vocalist mockup:
              Licensed catalogue (default) · My Trained Voices · Generation
              History. Tabs switch the top voice grid; length presets +
              generate panel stay visible for tabs 1 and 2, and the full
              history takes over the page in tab 3. */}
          <div data-testid="vocalist-tab-bar"
               className="mb-5 border-b flex items-center gap-6"
               style={{ borderColor: "#232632" }}>
            {[
              { id: "licensed", label: "Licensed Voice Library" },
              { id: "trained",  label: "My Trained Voices" },
              { id: "history",  label: "Generation History" },
            ].map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  data-testid={`vocalist-tab-${tab.id}`}
                  onClick={() => { setActiveTab(tab.id); setPage(1); }}
                  className="pb-3 -mb-px transition"
                  style={{
                    color: isActive ? "#ffffff" : "#a9b3c9",
                    borderBottom: isActive ? "2px solid #4a9eff" : "2px solid transparent",
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          {/* Search + refresh (hidden on history tab) */}
          {activeTab !== "history" && (
          <div className="flex items-center gap-2 mb-3">
            <div className="flex-1 relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2"
                      style={{ color: "#6c7180" }} />
              <input
                data-testid="vocalist-voice-search"
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder={activeTab === "trained"
                  ? "Search your trained voices…"
                  : "Search voice models by name, tag, genre…"}
                className="w-full rounded-lg pl-9 pr-3 py-2 outline-none"
                style={{ background: "#1a1e2a", border: "1px solid #232632", color: "#e6e9f0", fontSize: 13 }}
              />
            </div>
            <button data-testid="vocalist-refresh-voices"
                    onClick={loadVoices}
                    className="rounded-lg px-3 py-2"
                    style={{ background: "#1a1e2a", border: "1px solid #232632", color: "#c4c9d4" }}>
              <RefreshCw size={14} className={loadingVoices ? "animate-spin" : ""} />
            </button>
            <a href={activeTab === "trained" ? "https://www.app.kits.ai/voices/train" : "https://www.app.kits.ai/voices"}
               target="_blank" rel="noreferrer"
               data-testid="vocalist-browse-kits"
               className="rounded-lg px-3 py-2 type-caption"
               style={{ background: "#232632", color: "#4a9eff" }}>
              {activeTab === "trained" ? "Train a voice on Kits AI" : "Browse full Kits catalog"}{" "}
              <ExternalLink size={11} className="inline ml-1" />
            </a>
          </div>
          )}

          {/* Voice grid (hidden on history tab) */}
          {activeTab !== "history" && (
          <div data-testid="vocalist-voice-grid"
               className="grid gap-3 mb-6"
               style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
            {loadingVoices && voices.length === 0 ? (
              <div className="col-span-full text-center py-8 type-caption text-neutral-500">
                <Loader2 className="mx-auto mb-2 animate-spin" size={18} />
                Loading Kits AI voices…
              </div>
            ) : voices.length === 0 ? (
              <div className="col-span-full text-center py-8 type-caption text-neutral-500">
                {activeTab === "trained"
                  ? <>You haven&apos;t trained any voices on Kits AI yet. Head to Kits AI to clone your station&apos;s voice — it&apos;ll show up here automatically.</>
                  : <>No voices match &ldquo;{q}&rdquo;. Try a different search.</>}
              </div>
            ) : (
              voices.map((v) => (
                <VoiceCard key={v.id} voice={v}
                           selected={selectedVoice?.id === v.id}
                           onSelect={setSelectedVoice}
                           onPreview={previewVoice}
                           previewing={previewingId === v.id} />
              ))
            )}
          </div>
          )}

          {/* Pagination (hidden on history tab) */}
          {activeTab !== "history" && voicesMeta?.lastPage > 1 && (
            <div className="flex items-center justify-center gap-2 mb-8 type-caption">
              <button data-testid="vocalist-voices-prev"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="rounded px-2.5 py-1 disabled:opacity-40"
                      style={{ background: "#1a1e2a", border: "1px solid #232632", color: "#c4c9d4" }}>
                Prev
              </button>
              <span className="text-neutral-400">
                Page {voicesMeta.currentPage} of {voicesMeta.lastPage}
              </span>
              <button data-testid="vocalist-voices-next"
                      disabled={page >= (voicesMeta?.lastPage || 1)}
                      onClick={() => setPage((p) => p + 1)}
                      className="rounded px-2.5 py-1 disabled:opacity-40"
                      style={{ background: "#1a1e2a", border: "1px solid #232632", color: "#c4c9d4" }}>
                Next
              </button>
            </div>
          )}

          {/* ── Length Presets (mockup section) ─────────── */}
          {activeTab !== "history" && presets.length > 0 && (
            <div className="mb-6">
              <div className="type-caption uppercase tracking-wider text-neutral-500 mb-2">
                Length Preset
              </div>
              <div data-testid="vocalist-length-presets"
                   className="grid gap-3" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                {presets.map((p) => (
                  <LengthPresetCard
                    key={p.id}
                    preset={p}
                    selected={selectedPreset === p.id}
                    onSelect={(id) => {
                      setSelectedPreset(id);
                      const meta = presets.find((x) => x.id === id);
                      if (meta) setMaxOutputLength(meta.default);
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── Generate panel ────────────────────────────── */}
          {activeTab !== "history" && (
          <div ref={generatePanelRef}
               data-testid="vocalist-generate-panel"
               className="rounded-xl border p-5 mb-6"
               style={{ background: "#161923", borderColor: "#232632" }}>
            <div className="flex items-center justify-between mb-4">
              <div className="text-white" style={{ fontSize: 15, fontWeight: 600 }}>
                Generate
              </div>
              {selectedVoice && (
                <div className="type-caption text-neutral-400">
                  Target voice: <span style={{ color: "#4a9eff", fontWeight: 500 }}>{selectedVoice.title}</span>
                </div>
              )}
            </div>

            {/* File dropzone */}
            <label htmlFor="vocalist-scratch-file"
                   className="block rounded-lg border border-dashed p-6 text-center cursor-pointer mb-4"
                   style={{ background: "#1a1e2a", borderColor: scratchFile ? "#4a9eff" : "#232632" }}>
              <Upload className="mx-auto mb-2" size={20} style={{ color: "#4a9eff" }} />
              <div className="text-white" style={{ fontSize: 14, fontWeight: 500 }}>
                {scratchFile
                  ? `${scratchFile.name}${scratchDuration ? ` · ${scratchDuration.toFixed(1)}s` : ""}`
                  : `Upload a scratch vocal — max ${hardCapSec}s (WAV / MP3 / FLAC)`}
              </div>
              <div className="type-caption text-neutral-500 mt-1">
                Sing or hum the melody — Kits AI will map pitch + timing onto the selected voice.
              </div>
              <input id="vocalist-scratch-file"
                     data-testid="vocalist-scratch-file"
                     ref={fileInputRef}
                     type="file"
                     accept="audio/wav,audio/mpeg,audio/mp3,audio/flac,audio/x-wav,audio/x-flac"
                     className="hidden"
                     onChange={(e) => onPickScratch(e.target.files?.[0] || null)} />
            </label>

            {/* Update #166 — 3-column row: Target Style · Max Output Length · Send Output To */}
            <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
              <div>
                <div className="type-caption uppercase tracking-wider text-neutral-500 mb-1">
                  Target Style
                </div>
                <select
                  data-testid="vocalist-style"
                  value={selectedStyle}
                  onChange={(e) => setSelectedStyle(e.target.value)}
                  className="w-full rounded-lg px-3 py-2 outline-none"
                  style={{ background: "#1a1e2a", border: "1px solid #232632", color: "#e6e9f0", fontSize: 13 }}>
                  {styles.length === 0 ? (
                    <option value="match">Match original phrasing</option>
                  ) : (
                    styles.map((s) => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))
                  )}
                </select>
              </div>
              <div>
                <div className="type-caption uppercase tracking-wider text-neutral-500 mb-1">
                  Max Output Length
                </div>
                <div className="flex items-center gap-2">
                  <input
                    data-testid="vocalist-max-length"
                    type="range"
                    min={1}
                    max={hardCapSec}
                    step={1}
                    value={maxOutputLength}
                    onChange={(e) => setMaxOutputLength(Number(e.target.value))}
                    className="flex-1"
                    style={{ accentColor: "#7c5cff" }}
                  />
                  <span className="type-caption font-mono"
                        style={{ color: "#7c5cff", minWidth: 42, textAlign: "right" }}>
                    {maxOutputLength}s
                  </span>
                </div>
              </div>
              <div>
                <div className="type-caption uppercase tracking-wider text-neutral-500 mb-1">
                  Send Output To
                </div>
                <select
                  data-testid="vocalist-destination"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className="w-full rounded-lg px-3 py-2 outline-none"
                  style={{ background: "#1a1e2a", border: "1px solid #232632", color: "#e6e9f0", fontSize: 13 }}>
                  {OUTPUT_DESTINATIONS.map((d) => (
                    <option key={d.id} value={d.id}>{d.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-4">
              <div className="type-caption uppercase tracking-wider text-neutral-500 mb-1">
                Project name (optional)
              </div>
              <input
                data-testid="vocalist-project-name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder='e.g. "Station ID Hook — Morning Show"'
                className="w-full rounded-lg px-3 py-2 outline-none"
                style={{ background: "#1a1e2a", border: "1px solid #232632", color: "#e6e9f0", fontSize: 13 }}
              />
            </div>

            {/* Footer row */}
            <div className="flex items-center justify-between gap-3">
              <div className="type-caption text-neutral-500">
                <Zap size={11} className="inline mr-1" style={{ color: "#facc15" }} />
                Uses <b style={{ color: "#e6e9f0" }}>1 Kits AI credit</b> · BYOK, no BroadcastFlow charge
              </div>
              <button
                data-testid="vocalist-generate-btn"
                disabled={generating || !selectedVoice || !scratchFile}
                onClick={generate}
                className="rounded-full px-5 py-2 font-medium text-white transition disabled:opacity-50"
                style={{ background: "linear-gradient(135deg, #4a9eff 0%, #7c5cff 100%)" }}>
                {generating
                  ? (<><Loader2 className="inline animate-spin mr-2" size={14} /> Rendering…</>)
                  : (<><Sparkles className="inline mr-2" size={14} /> Generate Vocal</>)}
              </button>
            </div>
          </div>
          )}

          {/* ── Recent generations ─────────────────────────── */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <div className="text-white" style={{ fontSize: 15, fontWeight: 600 }}>
                Recent Generations
              </div>
              <button onClick={loadJobs} className="rounded p-1"
                      style={{ background: "#1a1e2a", border: "1px solid #232632", color: "#a9b3c9" }}
                      title="Refresh">
                <RefreshCw size={12} />
              </button>
            </div>
            <div data-testid="vocalist-history" className="space-y-2">
              {jobs.length === 0 ? (
                <div className="rounded-xl border p-6 text-center type-caption text-neutral-500"
                     style={{ background: "#1a1e2a", borderColor: "#232632" }}>
                  No generations yet. Pick a voice, upload a scratch vocal, and hit Generate.
                </div>
              ) : (
                jobs.map((j) => <GenerationRow key={j.job_id} job={j} onSendTo={onSendTo} />)
              )}
            </div>
          </div>

          {/* ── Compliance note ─────────────────────────── */}
          <div className="rounded-xl border p-4 flex items-start gap-3"
               style={{ background: "#161923", borderColor: "#232632" }}>
            <span className="rounded-full px-2 py-0.5 type-caption font-semibold uppercase tracking-wide"
                  style={{ background: "rgba(74,158,255,0.15)", color: "#4a9eff", fontSize: 10 }}>
              Pro Radio
            </span>
            <div className="type-caption text-neutral-400 flex-1">
              <b style={{ color: "#e6e9f0" }}>AI Vocalist Studio</b> requires a connected Kits AI
              account (BYOK) on Pro Radio, Network, and Enterprise plans. Every voice is licensed
              directly from the artist — no unauthorized cloning, no takedown risk. Output is
              capped at {hardCapSec} seconds to keep the tool focused on broadcast imaging assets.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
